import { ExclamationCircleOutlined } from '@ant-design/icons';
import { type MutationOptions, useApolloClient } from '@apollo/client';
import { Modal, message } from 'antd';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import getApolloErrors from '../../../../server/utils/getApolloErrors';

const useDeletionConfirm = <T, F>(
    baseTranslationKey: string,
    modalTranslationKey: string,
    messageTranslationKey: string
) => {
    const { t } = useTranslation([baseTranslationKey]);

    const apolloClient = useApolloClient();

    return useCallback(
        (options: MutationOptions<T, F>, onSuccess: () => void) => {
            Modal.confirm({
                title: t(`${baseTranslationKey}:${modalTranslationKey}.title`),
                icon: <ExclamationCircleOutlined />,
                content: t(`${baseTranslationKey}:${modalTranslationKey}.content`),
                okText: t(`${baseTranslationKey}:${modalTranslationKey}.okText`),
                okType: 'danger',
                cancelText: t(`${baseTranslationKey}:${modalTranslationKey}.cancelText`),
                async onOk() {
                    try {
                        // loading message
                        message.loading({
                            content: t(`${baseTranslationKey}:${messageTranslationKey}.deleteSubmitting`),
                            key: 'primary',
                            duration: 0,
                        });

                        // delete with the API
                        await apolloClient.mutate<T, F>(options).finally(() => {
                            message.destroy('primary');
                        });

                        message.success({
                            content: t(`${baseTranslationKey}:${messageTranslationKey}.deleteSuccessful`),
                            key: 'primary',
                        });

                        onSuccess();
                    } catch (error) {
                        const apolloErrors = getApolloErrors(error);
                        message.destroy('primary');

                        if (apolloErrors?.$root) {
                            message.error(apolloErrors?.$root);
                        } else {
                            message.error(t(`${baseTranslationKey}:${messageTranslationKey}.deleteFailed`));
                        }
                    }
                },
            });
        },
        [apolloClient, baseTranslationKey, messageTranslationKey, modalTranslationKey, t]
    );
};

export default useDeletionConfirm;
